import { useEffect, useMemo, useState } from "react"
import Select from "react-select"
import { singleSelectStyles } from "../../helpers/constant"
import {
    ARRAY_ITEMS_TYPE_OPTIONS,
    FIELD_TYPE_OPTIONS,
    SCHEMA_LIMITS,
    createEmptyField,
} from "./schemaConstants"

const typePillLabel = (field) => {
    if (field.type === "object") return `OBJECT · ${(field.fields || []).length}`
    if (field.type === "array") return `ARRAY · ${field.items_type || "string"}`
    return (field.type || "string").toUpperCase()
}

export default function SchemaFieldRow({
    field,
    index,
    onChange,
    onRemove,
    isDisabled = false,
    errors = {},
    depth = 1,
    isSortable = false,
    isDragging = false,
    isDragOver = false,
    onDragStart,
    onDragOver,
    onDragEnd,
}) {
    const hasError = useMemo(
        () => Object.keys(errors).some((k) => k.startsWith(`${field.id}.`)),
        [errors, field.id]
    )

    const [isExpanded, setIsExpanded] = useState(() => !field.name || hasError)

    useEffect(() => {
        if (hasError) setIsExpanded(true)
    }, [hasError])

    const nameError = errors[`${field.id}.name`]
    const descriptionError = errors[`${field.id}.description`]
    const optionsError = errors[`${field.id}.options`]
    const itemsTypeError = errors[`${field.id}.items_type`]
    const fieldsError = errors[`${field.id}.fields`]

    const handleNameChange = (value) => {
        const converted = value.toLowerCase().replace(/\s+/g, "_")
        onChange(field.id, { name: converted })
    }

    const handleTypeChange = (option) => {
        if (!option) return
        const newType = option.value
        if (newType === field.type) return

        if (
            field.type === "object" &&
            newType !== "object" &&
            (field.fields || []).length > 0
        ) {
            const ok = window.confirm(
                "Switching away from Object will discard all nested fields. Continue?"
            )
            if (!ok) return
        }

        const updates = { type: newType }
        if (newType !== "enum") updates.options = undefined
        if (newType !== "array") updates.items_type = undefined
        if (newType !== "object") updates.fields = undefined
        if (newType === "enum") updates.options = []
        if (newType === "array") updates.items_type = "string"
        if (newType === "object") updates.fields = []

        onChange(field.id, updates)
    }

    const handleEnumKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault()
            const val = e.target.value.trim()
            if (!val) return
            if (val.length > SCHEMA_LIMITS.ENUM_OPTION_LENGTH_MAX) return
            if ((field.options || []).length >= SCHEMA_LIMITS.ENUM_OPTIONS_MAX) return
            if ((field.options || []).includes(val)) return
            onChange(field.id, { options: [...(field.options || []), val] })
            e.target.value = ""
        }
    }

    const removeEnumOption = (optionToRemove) => {
        onChange(field.id, {
            options: (field.options || []).filter((o) => o !== optionToRemove),
        })
    }

    const nested = field.fields || []
    const canAddNested =
        nested.length < SCHEMA_LIMITS.MAX_FIELDS &&
        depth < SCHEMA_LIMITS.MAX_NESTING_DEPTH

    const handleNestedFieldChange = (nestedId, updates) => {
        onChange(field.id, {
            fields: (field.fields || []).map((f) =>
                f.id === nestedId ? { ...f, ...updates } : f
            ),
        })
    }

    const handleNestedFieldRemove = (nestedId) => {
        onChange(field.id, {
            fields: (field.fields || []).filter((f) => f.id !== nestedId),
        })
    }

    const handleAddNestedField = () => {
        if (!canAddNested) return
        onChange(field.id, {
            fields: [...nested, createEmptyField()],
        })
    }

    const wrapperClass = [
        "border rounded-xl mb-3 bg-white transition",
        hasError ? "border-red-300" : "border-gray-200",
        isDragging ? "opacity-50" : "",
        isDragOver ? "ring-2 ring-indigo-300" : "",
    ]
        .filter(Boolean)
        .join(" ")

    return (
        <div
            className={wrapperClass}
            draggable={isSortable && !isDisabled}
            onDragStart={isSortable ? () => onDragStart?.(index) : undefined}
            onDragOver={
                isSortable
                    ? (e) => {
                          e.preventDefault()
                          onDragOver?.(index)
                      }
                    : undefined
            }
            onDragEnd={isSortable ? () => onDragEnd?.() : undefined}
        >
            {/* Header (click anywhere to toggle) */}
            <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                onClick={() => setIsExpanded((v) => !v)}
            >
                {/* Chevron */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${
                        isExpanded ? "rotate-90" : ""
                    }`}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>

                {/* Grip (top-level only) */}
                {isSortable && (
                    <span
                        className="cursor-grab text-gray-400 hover:text-gray-600 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        title="Drag to reorder"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <circle cx="5" cy="3" r="1.5" />
                            <circle cx="11" cy="3" r="1.5" />
                            <circle cx="5" cy="8" r="1.5" />
                            <circle cx="11" cy="8" r="1.5" />
                            <circle cx="5" cy="13" r="1.5" />
                            <circle cx="11" cy="13" r="1.5" />
                        </svg>
                    </span>
                )}

                <span className="text-sm font-medium text-gray-500 shrink-0">
                    Field {index + 1}
                </span>

                {/* Field name */}
                <span
                    className={`min-w-0 flex-1 truncate pr-1 text-sm ${
                        field.name
                            ? "text-zinc-900 font-semibold"
                            : "text-zinc-400 font-normal"
                    }`}
                >
                    {field.name || "Unnamed field"}
                </span>

                {/* Type pill */}
                <span className="shrink-0 inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-mono font-medium text-gray-700">
                    {typePillLabel(field)}
                </span>

                {/* Required pill */}
                {field.required && (
                    <span className="shrink-0 inline-flex items-center rounded-md bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[11px] font-medium">
                        Required
                    </span>
                )}

                {/* Error icon */}
                {hasError && (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-4 h-4 text-red-500 shrink-0"
                        title="This field has validation errors"
                    >
                        <path
                            fillRule="evenodd"
                            d="M9.401 3.003c1.155-2.005 4.043-2.005 5.197 0l7.355 12.748c1.154 2.005-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.495-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                            clipRule="evenodd"
                        />
                    </svg>
                )}

                {/* Trash */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        onRemove(field.id)
                    }}
                    disabled={isDisabled}
                    className="text-gray-400 hover:text-red-500 transition shrink-0"
                    title="Remove field"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                </button>
            </div>

            {/* Body */}
            {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-12 gap-3">
                        {/* Field Name */}
                        <div className="col-span-3">
                            <label className="text-xs text-gray-500 mb-1 block">
                                Field Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={field.name}
                                onChange={(e) => handleNameChange(e.target.value)}
                                maxLength={SCHEMA_LIMITS.FIELD_NAME_MAX}
                                placeholder="field_name"
                                disabled={isDisabled}
                                className={`w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black ${
                                    nameError ? "border-red-400" : "border-gray-300"
                                }`}
                            />
                            {nameError && (
                                <p className="mt-1 text-xs text-red-500">{nameError}</p>
                            )}
                        </div>

                        {/* Type */}
                        <div className="col-span-3">
                            <label className="text-xs text-gray-500 mb-1 block">
                                Type <span className="text-red-500">*</span>
                            </label>
                            <Select
                                options={FIELD_TYPE_OPTIONS}
                                value={FIELD_TYPE_OPTIONS.find((o) => o.value === field.type)}
                                onChange={handleTypeChange}
                                styles={singleSelectStyles}
                                isSearchable={false}
                                isDisabled={isDisabled}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>

                        {/* Description */}
                        <div className="col-span-4">
                            <label className="text-xs text-gray-500 mb-1 block">Description</label>
                            <input
                                type="text"
                                value={field.description || ""}
                                onChange={(e) => onChange(field.id, { description: e.target.value })}
                                maxLength={SCHEMA_LIMITS.DESCRIPTION_MAX}
                                placeholder="Describe this field..."
                                disabled={isDisabled}
                                className={`w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black ${
                                    descriptionError ? "border-red-400" : "border-gray-300"
                                }`}
                            />
                            {descriptionError && (
                                <p className="mt-1 text-xs text-red-500">{descriptionError}</p>
                            )}
                        </div>

                        {/* Required Toggle */}
                        <div className="col-span-2 flex flex-col items-center justify-end">
                            <label className="text-xs text-gray-500 mb-1 block">Required</label>
                            <button
                                type="button"
                                onClick={() => onChange(field.id, { required: !field.required })}
                                disabled={isDisabled}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                                    field.required ? "bg-indigo-600" : "bg-gray-200"
                                }`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                                        field.required ? "translate-x-5" : "translate-x-1"
                                    }`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Enum Options */}
                    {field.type === "enum" && (
                        <div className="mt-3">
                            <label className="text-xs text-gray-500 mb-1 block">
                                Enum Options <span className="text-red-500">*</span>
                                <span className="text-gray-400 ml-1">
                                    ({(field.options || []).length}/{SCHEMA_LIMITS.ENUM_OPTIONS_MAX})
                                </span>
                            </label>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {(field.options || []).map((opt, i) => (
                                    <span
                                        key={i}
                                        className="inline-flex items-center gap-1 bg-gray-100 text-sm px-2.5 py-1 rounded-full"
                                    >
                                        {opt}
                                        <button
                                            type="button"
                                            onClick={() => removeEnumOption(opt)}
                                            className="text-gray-400 hover:text-red-500"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <input
                                type="text"
                                placeholder="Type an option and press Enter"
                                onKeyDown={handleEnumKeyDown}
                                disabled={
                                    isDisabled ||
                                    (field.options || []).length >= SCHEMA_LIMITS.ENUM_OPTIONS_MAX
                                }
                                className={`w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black ${
                                    optionsError ? "border-red-400" : "border-gray-300"
                                }`}
                            />
                            {optionsError && (
                                <p className="mt-1 text-xs text-red-500">{optionsError}</p>
                            )}
                        </div>
                    )}

                    {/* Array Items Type */}
                    {field.type === "array" && (
                        <div className="mt-3">
                            <label className="text-xs text-gray-500 mb-1 block">
                                Array Items Type <span className="text-red-500">*</span>
                            </label>
                            <div className="w-48">
                                <Select
                                    options={ARRAY_ITEMS_TYPE_OPTIONS}
                                    value={ARRAY_ITEMS_TYPE_OPTIONS.find(
                                        (o) => o.value === field.items_type
                                    )}
                                    onChange={(option) =>
                                        onChange(field.id, { items_type: option.value })
                                    }
                                    styles={singleSelectStyles}
                                    isSearchable={false}
                                    isDisabled={isDisabled}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            {itemsTypeError && (
                                <p className="mt-1 text-xs text-red-500">{itemsTypeError}</p>
                            )}
                        </div>
                    )}

                    {/* Object Nested Editor */}
                    {field.type === "object" && (
                        <div className="mt-4 pl-4 border-l-2 border-indigo-200">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-600">
                                    Nested Fields ({nested.length}/{SCHEMA_LIMITS.MAX_FIELDS})
                                </span>
                                <span className="text-xs text-gray-400">
                                    depth {depth}/{SCHEMA_LIMITS.MAX_NESTING_DEPTH}
                                </span>
                            </div>

                            {fieldsError && (
                                <p className="mb-2 text-xs text-red-500">{fieldsError}</p>
                            )}

                            {nested.length === 0 && (
                                <p className="text-xs text-gray-400 mb-2">
                                    No nested fields yet. Add at least one.
                                </p>
                            )}

                            {nested.map((nf, ni) => (
                                <SchemaFieldRow
                                    key={nf.id}
                                    field={nf}
                                    index={ni}
                                    onChange={handleNestedFieldChange}
                                    onRemove={handleNestedFieldRemove}
                                    isDisabled={isDisabled}
                                    errors={errors}
                                    depth={depth + 1}
                                    isSortable={false}
                                />
                            ))}

                            <button
                                type="button"
                                onClick={handleAddNestedField}
                                disabled={isDisabled || !canAddNested}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                                title={
                                    depth >= SCHEMA_LIMITS.MAX_NESTING_DEPTH
                                        ? `Max nesting depth is ${SCHEMA_LIMITS.MAX_NESTING_DEPTH}`
                                        : nested.length >= SCHEMA_LIMITS.MAX_FIELDS
                                        ? `Max ${SCHEMA_LIMITS.MAX_FIELDS} nested fields`
                                        : ""
                                }
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    stroke="currentColor"
                                    className="w-4 h-4"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                                Add nested field
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
